import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, Button, Chip, CircularProgress, Divider, IconButton, Snackbar, TextField, Tooltip, Typography } from '@material-ui/core';
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import ThumbUpOutlinedIcon from '@material-ui/icons/ThumbUpAltOutlined';
import ThumbDownIcon from '@material-ui/icons/ThumbDown';
import ThumbDownOutlinedIcon from '@material-ui/icons/ThumbDownAltOutlined';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import NotificationsNoneIcon from '@material-ui/icons/NotificationsNone';
import { Content, MarkdownContent, Page } from '@backstage/core-components';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';

interface ItemMeta {
  technology: string;
  area: string;
  category: string;
  stage: string;
  open_source_commercial: string;
  recommendation: string;
  costs: string;
  risks: string;
}

interface BlipComment {
  id: number;
  parent_id: number | null;
  body: string;
  author: string;
  author_ref: string;
  created_at: string;
}

interface BlipVotes {
  up: number;
  down: number;
  myVote: number; // 1, -1, or 0
}

const STAGE_COLORS: Record<string, string> = {
  Provisioned: '#81c784',
  Trial: '#64b5f6',
  Assess: '#ffd54f',
  Deprecated: '#e57373',
};

const CATEGORY_COLORS: Record<string, string> = {
  Platform: '#82b1ff',
  Platforms: '#82b1ff',
  Tools: '#80cbc4',
  'Shared Services': '#ce93d8',
  Techniques: '#ffcc80',
};

export const TechRadarBlipPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const fromTabLabel = (location.state as any)?.fromTabLabel ?? 'Tech Radar';
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [meta, setMeta] = useState<ItemMeta | null>(null);
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);

  // Votes state
  const [votes, setVotes] = useState<BlipVotes>({ up: 0, down: 0, myVote: 0 });
  const [voteSubmitting, setVoteSubmitting] = useState(false);

  // Comments state
  const [comments, setComments] = useState<BlipComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentInput, setCommentInput] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [commentError, setCommentError] = useState('');
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [replyInput, setReplyInput] = useState('');
  const [replySubmitting, setReplySubmitting] = useState(false);

  // Snackbar notification state
  const [snackMsg, setSnackMsg] = useState('');
  // Track previous comment count for new-comment notification during polling
  const prevCommentCountRef = useRef<number>(-1);

  // Subscription state
  const [subscribed, setSubscribed] = useState(false);
  const [subLoading, setSubLoading] = useState(false);

  const fetchSubscription = useCallback(async () => {
    if (!slug) return;
    try {
      const base = await discoveryApi.getBaseUrl('tech-radar');
      const resp = await fetchApi.fetch(`${base}/blips/${slug}/subscription`);
      if (resp.ok) {
        const data = await resp.json();
        setSubscribed(data.subscribed ?? false);
      }
    } catch { /* ignore */ }
  }, [slug, discoveryApi, fetchApi]);

  const toggleSubscription = async () => {
    if (!slug || subLoading) return;
    setSubLoading(true);
    try {
      const base = await discoveryApi.getBaseUrl('tech-radar');
      const method = subscribed ? 'DELETE' : 'POST';
      const resp = await fetchApi.fetch(`${base}/blips/${slug}/subscribe`, { method });
      if (resp.ok) {
        const data = await resp.json();
        setSubscribed(data.subscribed ?? !subscribed);
        setSnackMsg(data.subscribed ? 'Subscribed — you’ll be notified of new comments' : 'Unsubscribed from this blip');
      }
    } catch { /* ignore */ }
    setSubLoading(false);
  };

  const fetchVotes = useCallback(async () => {
    if (!slug) return;
    try {
      const base = await discoveryApi.getBaseUrl('tech-radar');
      const resp = await fetchApi.fetch(`${base}/blips/${slug}/votes`);
      if (resp.ok) setVotes(await resp.json());
    } catch { /* ignore */ }
  }, [slug, discoveryApi, fetchApi]);

  const castVote = async (value: 1 | -1) => {
    if (!slug || voteSubmitting) return;
    setVoteSubmitting(true);
    // Optimistic update
    setVotes(prev => {
      const removing = prev.myVote === value;
      const wasOpposite = prev.myVote !== 0 && prev.myVote !== value;
      return {
        up: value === 1 ? (removing ? prev.up - 1 : prev.up + 1) : (wasOpposite ? prev.up - 1 : prev.up),
        down: value === -1 ? (removing ? prev.down - 1 : prev.down + 1) : (wasOpposite ? prev.down - 1 : prev.down),
        myVote: removing ? 0 : value,
      };
    });
    try {
      const base = await discoveryApi.getBaseUrl('tech-radar');
      const resp = await fetchApi.fetch(`${base}/blips/${slug}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vote: value }),
      });
      if (resp.ok) setVotes(await resp.json());
      else await fetchVotes(); // rollback optimistic
    } catch { await fetchVotes(); }
    setVoteSubmitting(false);
  };

  const fetchComments = useCallback(async (isPolling = false) => {
    if (!slug) return;
    if (!isPolling) setCommentsLoading(true);
    try {
      const base = await discoveryApi.getBaseUrl('tech-radar');
      const resp = await fetchApi.fetch(`${base}/blips/${slug}/comments`);
      if (resp.ok) {
        const { comments: list } = await resp.json();
        const fetched: BlipComment[] = list ?? [];
        // During polling: notify if new top-level comments appeared
        if (isPolling && prevCommentCountRef.current >= 0 && fetched.length > prevCommentCountRef.current) {
          const newest = fetched[fetched.length - 1];
          setSnackMsg(`New comment from ${newest?.author ?? 'someone'}`);
        }
        prevCommentCountRef.current = fetched.length;
        setComments(fetched);
      }
    } catch { /* ignore */ }
    if (!isPolling) setCommentsLoading(false);
  }, [slug, discoveryApi, fetchApi]);

  const submitComment = async (parentId: number | null = null) => {
    const body = parentId === null ? commentInput.trim() : replyInput.trim();
    if (!body || !slug) return;
    if (parentId === null) setCommentSubmitting(true);
    else setReplySubmitting(true);
    setCommentError('');
    try {
      const base = await discoveryApi.getBaseUrl('tech-radar');
      const resp = await fetchApi.fetch(`${base}/blips/${slug}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body, parent_id: parentId }),
      });
      if (resp.ok) {
        if (parentId === null) setCommentInput('');
        else { setReplyInput(''); setReplyingTo(null); }
        await fetchComments();
        setSnackMsg('Comment posted!');
      } else {
        const data = await resp.json().catch(() => ({}));
        setCommentError((data as any)?.error?.message ?? 'Failed to submit comment');
      }
    } catch {
      setCommentError('Failed to submit comment');
    }
    if (parentId === null) setCommentSubmitting(false);
    else setReplySubmitting(false);
  };

  useEffect(() => {
    if (!slug) return;
    discoveryApi.getBaseUrl('tech-radar').then(async base => {
      const [metaResp, blipResp] = await Promise.all([
        fetchApi.fetch(`${base}/items/by-slug/${slug}`),
        fetchApi.fetch(`${base}/blips/${slug}`),
      ]);
      if (metaResp.ok) {
        const { item } = await metaResp.json();
        setMeta(item);
      } else {
        setNotFound(true);
      }
      if (blipResp.ok) {
        setMarkdown(await blipResp.text());
      }
      setLoading(false);
    }).catch(() => { setNotFound(true); setLoading(false); });
    fetchComments();
    fetchVotes();
    fetchSubscription();
  }, [slug, discoveryApi, fetchApi, fetchComments, fetchVotes, fetchSubscription]);

  // 30-second polling for new comments (shows Snackbar when new comments arrive)
  useEffect(() => {
    if (!slug) return undefined;
    const interval = setInterval(() => fetchComments(true), 30_000);
    return () => clearInterval(interval);
  }, [slug, fetchComments]);

  // Build threaded comment tree
  const topLevel = comments.filter(c => !c.parent_id);
  const getReplies = (parentId: number) => comments.filter(c => c.parent_id === parentId);

  const renderComment = (c: BlipComment, isReply = false) => (
    <Box key={c.id} mb={isReply ? 1 : 2} ml={isReply ? 3 : 0}>
      <Box
        p={2}
        style={{
          background: isReply ? 'rgba(255,255,255,0.025)' : 'rgba(255,255,255,0.04)',
          borderRadius: 6,
          border: `1px solid ${isReply ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.08)'}`,
          borderLeft: isReply ? '2px solid rgba(144,202,249,0.3)' : undefined,
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <Typography variant="caption" style={{ color: '#90caf9', fontWeight: 600 }}>
              {c.author}
            </Typography>
            <Typography variant="caption" style={{ color: 'rgba(255,255,255,0.3)' }}>
              {new Date(c.created_at).toLocaleString()}
            </Typography>
          </Box>
          {!isReply && (
            <Button
              size="small"
              style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, padding: '2px 8px', minWidth: 0 }}
              onClick={() => setReplyingTo(replyingTo === c.id ? null : c.id)}
            >
              {replyingTo === c.id ? 'Cancel' : 'Reply'}
            </Button>
          )}
        </Box>
        <Typography variant="body2" style={{ color: 'rgba(255,255,255,0.8)', whiteSpace: 'pre-wrap', marginTop: 4 }}>
          {c.body}
        </Typography>
      </Box>

      {/* Inline reply form */}
      {replyingTo === c.id && (
        <Box mt={1} ml={3}>
          <TextField
            multiline
            minRows={2}
            fullWidth
            placeholder={`Reply to ${c.author}…`}
            value={replyInput}
            onChange={e => setReplyInput(e.target.value)}
            inputProps={{ maxLength: 2000 }}
            variant="outlined"
            style={{ marginBottom: 6 }}
            InputProps={{ style: { color: '#fff', background: 'rgba(255,255,255,0.04)', fontSize: 13 } }}
          />
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={!replyInput.trim() || replySubmitting}
              onClick={() => submitComment(c.id)}
            >
              {replySubmitting ? 'Posting…' : 'Post reply'}
            </Button>
          </Box>
        </Box>
      )}

      {/* Nested replies */}
      {getReplies(c.id).map(reply => renderComment(reply, true))}
    </Box>
  );

  const MetaRow = ({ label, value, colorMap }: { label: string; value: string; colorMap?: Record<string, string> }) => {
    if (!value) return null;
    const bg = colorMap?.[value];
    return (
      <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 6 }}>
        <Typography variant="caption" style={{ color: 'rgba(255,255,255,0.45)', minWidth: 90 }}>{label}</Typography>
        {bg ? (
          <Chip label={value} size="small" style={{ background: bg, color: '#000', fontWeight: 600, fontSize: 11 }} />
        ) : (
          <Typography variant="body2" style={{ color: 'rgba(255,255,255,0.85)' }}>{value}</Typography>
        )}
      </Box>
    );
  };

  return (
    <Page themeId="tool">
      <Content>
        <Box style={{ background: '#111', margin: '-24px', padding: '24px', minHeight: 'calc(100vh - 150px)' }}>
        <Box mb={3} display="flex" alignItems="center" justifyContent="space-between">
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate(-1)}
            style={{ color: '#90caf9', borderColor: '#90caf9' }}
          >
            ← Back to {fromTabLabel}
          </Button>
          <Tooltip title={subscribed ? 'Unsubscribe from comment notifications' : 'Subscribe to get notified of new comments'}>
            <span>
              <Button
                size="small"
                variant={subscribed ? 'contained' : 'outlined'}
                startIcon={subscribed ? <NotificationsActiveIcon /> : <NotificationsNoneIcon />}
                disabled={subLoading || loading}
                onClick={toggleSubscription}
                style={subscribed
                  ? { background: '#1565c0', color: '#fff', borderColor: '#1565c0' }
                  : { color: 'rgba(255,255,255,0.55)', borderColor: 'rgba(255,255,255,0.25)' }
                }
              >
                {subscribed ? 'Subscribed' : 'Subscribe'}
              </Button>
            </span>
          </Tooltip>
        </Box>

        {loading && (
          <Box display="flex" justifyContent="center" mt={6}>
            <CircularProgress />
          </Box>
        )}

        {!loading && notFound && !meta && (
          <Typography style={{ color: 'rgba(255,255,255,0.5)', marginTop: 32 }}>
            Technology not found.
          </Typography>
        )}

        {meta && (
          <Box
            mb={3}
            p={3}
            style={{
              background: 'rgba(255,255,255,0.04)',
              borderRadius: 8,
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <Typography variant="h4" style={{ color: '#fff', fontWeight: 700, marginBottom: 20 }}>
              {meta.technology}
            </Typography>
            <MetaRow label="Stage" value={meta.stage} colorMap={STAGE_COLORS} />
            <MetaRow label="Quadrant" value={meta.category} colorMap={CATEGORY_COLORS} />
            <MetaRow label="Area" value={meta.area} />
            <MetaRow label="License" value={meta.open_source_commercial} />
            <MetaRow label="Recommendation" value={meta.recommendation} />
            <MetaRow label="Costs" value={meta.costs} />
            {meta.risks && (
              <>
                <Divider style={{ background: 'rgba(255,255,255,0.1)', margin: '12px 0' }} />
                <Typography variant="body2" style={{ color: 'rgba(255,255,255,0.7)' }}>{meta.risks}</Typography>
              </>
            )}
          </Box>
        )}

        {markdown && (
          <>
            <Divider style={{ background: 'rgba(255,255,255,0.1)', marginBottom: 16 }} />
            <MarkdownContent content={markdown} />
          </>
        )}

        {/* ── Votes ── */}
        {!loading && (meta || markdown) && (
          <Box
            mt={4}
            p={2}
            display="flex"
            alignItems="center"
            style={{
              background: 'rgba(255,255,255,0.03)',
              borderRadius: 8,
              border: '1px solid rgba(255,255,255,0.08)',
              gap: 24,
            }}
          >
            <Typography variant="body2" style={{ color: 'rgba(255,255,255,0.5)', marginRight: 8 }}>
              Vote about blip:
            </Typography>
            <Box display="flex" alignItems="center" style={{ gap: 6 }}>
              <Tooltip title={votes.myVote === 1 ? 'Remove thumbs up' : 'Thumbs up'}>
                <span>
                  <IconButton
                    size="small"
                    disabled={voteSubmitting}
                    onClick={() => castVote(1)}
                    style={{ color: votes.myVote === 1 ? '#81c784' : 'rgba(255,255,255,0.4)' }}
                  >
                    {votes.myVote === 1 ? <ThumbUpIcon fontSize="small" /> : <ThumbUpOutlinedIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
              <Typography variant="body2" style={{ color: votes.myVote === 1 ? '#81c784' : 'rgba(255,255,255,0.6)', fontWeight: 600, minWidth: 16, textAlign: 'center' }}>
                {votes.up}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" style={{ gap: 6 }}>
              <Tooltip title={votes.myVote === -1 ? 'Remove thumbs down' : 'Thumbs down'}>
                <span>
                  <IconButton
                    size="small"
                    disabled={voteSubmitting}
                    onClick={() => castVote(-1)}
                    style={{ color: votes.myVote === -1 ? '#ef9a9a' : 'rgba(255,255,255,0.4)' }}
                  >
                    {votes.myVote === -1 ? <ThumbDownIcon fontSize="small" /> : <ThumbDownOutlinedIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
              <Typography variant="body2" style={{ color: votes.myVote === -1 ? '#ef9a9a' : 'rgba(255,255,255,0.6)', fontWeight: 600, minWidth: 16, textAlign: 'center' }}>
                {votes.down}
              </Typography>
            </Box>
          </Box>
        )}

        {/* ── Discussion ── */}
        {!loading && (meta || markdown) && (
          <Box mt={4}>
            <Divider style={{ background: 'rgba(255,255,255,0.1)', marginBottom: 24 }} />
            <Typography variant="h6" style={{ color: '#fff', fontWeight: 600, marginBottom: 16 }}>
              Discussion
            </Typography>

            {commentsLoading && (
              <Box display="flex" justifyContent="center" my={2}>
                <CircularProgress size={20} />
              </Box>
            )}
            {!commentsLoading && comments.length === 0 && (
              <Typography variant="body2" style={{ color: 'rgba(255,255,255,0.35)', marginBottom: 16 }}>
                No comments yet. Be the first to ask a question or share a note.
              </Typography>
            )}

            {topLevel.map(c => renderComment(c))}

            {/* New top-level comment form */}
            <Box mt={3}>
              <TextField
                multiline
                minRows={3}
                fullWidth
                placeholder="Ask a question or leave a note…"
                value={commentInput}
                onChange={e => setCommentInput(e.target.value)}
                inputProps={{ maxLength: 2000 }}
                variant="outlined"
                style={{ marginBottom: 8 }}
                InputProps={{ style: { color: '#fff', background: 'rgba(255,255,255,0.06)' } }}
              />
              {commentError && (
                <Typography variant="caption" style={{ color: '#ef9a9a', display: 'block', marginBottom: 6 }}>
                  {commentError}
                </Typography>
              )}
              <Box display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  disabled={!commentInput.trim() || commentSubmitting}
                  onClick={() => submitComment(null)}
                >
                  {commentSubmitting ? 'Posting…' : 'Post comment'}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
        </Box>
      </Content>
      <Snackbar
        open={!!snackMsg}
        autoHideDuration={4000}
        onClose={() => setSnackMsg('')}
        message={snackMsg}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Page>
  );
};
